import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import TeamDetailModal from './TeamDetailModal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TeamStat {
  position: number;
  team: string;
  shortName: string;
  crest: string;
  teamId?: number;
  played: number;
  won: number;
  draw: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
  form: string[];
}

interface PlayerStat {
  id: string;
  name: string;
  shortName: string;
  photo: string;
  teamName: string;
  teamCrest: string;
  position: string;
  nationality: string;
  goals: number;
  assists: number;
  penalties?: number;
  shots: number;
  saves: number;
  yellowCards: number;
  played: number;
  _leagueFlag?: string;
  _leagueLabel?: string;
}

interface SelectedTeam {
  teamId: number;
  teamName: string;
  teamCrest: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const LEAGUES = [
  { label: 'LaLiga',     slug: 'laliga',     flag: '🇪🇸', accent: '#ee8700' },
  { label: 'Premier',    slug: 'premier',    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', accent: '#3d1099' },
  { label: 'Serie A',    slug: 'seriea',     flag: '🇮🇹', accent: '#0070b9' },
  { label: 'Bundesliga', slug: 'bundesliga', flag: '🇩🇪', accent: '#d20515' },
  { label: 'Ligue 1',    slug: 'ligue1',     flag: '🇫🇷', accent: '#024494' },
];

type SortKey =
  | 'points' | 'goalsFor' | 'goalsAgainst' | 'goalDiff'
  | 'won' | 'played' | 'goals' | 'assists' | 'shots' | 'saves' | 'yellowCards';

const PLAYER_CATS = [
  { id: 'goals',       label: 'Goles',        icon: 'ri-football-line',       color: '#00e87a' },
  { id: 'assists',     label: 'Asistencias',  icon: 'ri-record-circle-line',  color: '#ffd700' },
  { id: 'shots',       label: 'Disparos',     icon: 'ri-cursor-line',         color: '#ff6b35' },
  { id: 'saves',       label: 'Paradas',      icon: 'ri-shield-line',         color: '#60a5fa' },
  { id: 'yellowCards', label: 'Tarjetas',     icon: 'ri-flashlight-line',     color: '#f59e0b' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function FormDot({ result }: { result: string }) {
  const colors: Record<string, string> = { W: '#00e87a', D: '#ffd700', L: '#ef4444' };
  return (
    <span
      className="inline-block w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center"
      style={{ background: `${colors[result] ?? '#3d4149'}20`, color: colors[result] ?? '#6b7280' }}
    >
      {result}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  if (plan === 'elite')   return <span className="px-2 py-0.5 rounded-full bg-[#ff6b35]/15 text-[#ff6b35] text-xs font-bold">Elite</span>;
  if (plan === 'premium') return <span className="px-2 py-0.5 rounded-full bg-[#ffd700]/15 text-[#ffd700] text-xs font-bold">Premium</span>;
  return <span className="px-2 py-0.5 rounded-full bg-white/5 text-[#6b7280] text-xs font-bold">Gratuito</span>;
}

function LockOverlay({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div className="relative">
      <div className="select-none pointer-events-none blur-sm opacity-30">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-[#1e2028]">
            <div className="h-4 w-5 bg-[#2a2d3a] rounded"></div>
            <div className="w-8 h-8 rounded-full bg-[#2a2d3a]"></div>
            <div className="flex-1 h-4 bg-[#2a2d3a] rounded max-w-[150px]"></div>
            <div className="ml-auto flex gap-5">
              {Array.from({ length: 6 }).map((_, j) => <div key={j} className="h-4 w-8 bg-[#2a2d3a] rounded"></div>)}
            </div>
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0d0f14]/85 backdrop-blur-[2px] p-6">
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#ffd700]/15 mb-4">
          <i className="ri-vip-crown-line text-[#ffd700] text-2xl"></i>
        </div>
        <p className="text-white font-black text-lg mb-1">Stats Pro Completas</p>
        <p className="text-[#a8b2c1] text-sm text-center max-w-xs mb-4 leading-relaxed">
          Accede a estadísticas detalladas de equipos y jugadores de las 5 grandes ligas europeas con{' '}
          <strong className="text-[#ffd700]">Premium</strong>.
        </p>
        <ul className="text-[#6b7280] text-xs text-center space-y-1 mb-5">
          <li>Tabla completa: victorias, goles, dif., forma</li>
          <li>Líderes en goles, asistencias, disparos, paradas</li>
          <li>Búsqueda de jugadores en las 5 ligas</li>
          <li>Fichas de equipo: plantilla, entrenador, sede</li>
        </ul>
        <button
          onClick={onUpgrade}
          className="px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all whitespace-nowrap"
        >
          Activar Premium — 2,50€/mes
        </button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function StatsProSection() {
  const { isPremium, plan, openPaywallModal, isRegistered, openLoginModal } = useAuth();

  const [activeLeague, setActiveLeague] = useState(LEAGUES[0]);
  const [subTab, setSubTab]             = useState<'equipos' | 'jugadores'>('equipos');
  const [sortKey, setSortKey]           = useState<SortKey>('points');
  const [sortAsc, setSortAsc]           = useState(false);
  const [playerCat, setPlayerCat]       = useState('goals');

  // Teams
  const [teams, setTeams]               = useState<TeamStat[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamError, setTeamError]       = useState<string | null>(null);
  const [teamsSource, setTeamsSource]   = useState('football-data.org');
  const [imgErrors, setImgErrors]       = useState<Record<string, boolean>>({});

  // Team detail modal
  const [selectedTeam, setSelectedTeam] = useState<SelectedTeam | null>(null);

  // Players
  const [players, setPlayers]               = useState<PlayerStat[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playerError, setPlayerError]       = useState<string | null>(null);
  const [playersSource, setPlayersSource]   = useState('football-data.org');

  // Search
  const [playerSearch, setPlayerSearch]               = useState('');
  const [allLeaguesMode, setAllLeaguesMode]           = useState(false);
  const [allLeaguesPlayers, setAllLeaguesPlayers]     = useState<PlayerStat[]>([]);
  const [loadingAllLeagues, setLoadingAllLeagues]     = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

  const handleUpgrade = () => {
    if (!isRegistered) openLoginModal(); else openPaywallModal('premium');
  };

  // ── Fetch standings ────────────────────────────────────────────────────────
  const fetchTeams = useCallback(async (slug: string) => {
    setLoadingTeams(true);
    setTeamError(null);
    try {
      const res  = await fetch(`${supabaseUrl}/functions/v1/football-stats?type=standings&league=${slug}&season=2024`);
      const data = await res.json();
      if (data.status === 'ok' && Array.isArray(data.data)) {
        setTeams(data.data);
        setTeamsSource(data.source ?? 'football-data.org');
      } else {
        setTeamError(data.message ?? 'No se pudieron cargar los datos.');
      }
    } catch {
      setTeamError('Error de conexión.');
    } finally {
      setLoadingTeams(false);
    }
  }, [supabaseUrl]);

  // ── Fetch players (single league) ─────────────────────────────────────────
  const fetchPlayers = useCallback(async (slug: string, category: string) => {
    setLoadingPlayers(true);
    setPlayerError(null);
    try {
      const res  = await fetch(`${supabaseUrl}/functions/v1/football-stats?type=players&league=${slug}&category=${category}&season=2024`);
      const data = await res.json();
      if (data.status === 'ok' && Array.isArray(data.data)) {
        setPlayers(data.data);
        setPlayersSource(data.source ?? 'football-data.org');
      } else {
        setPlayerError(data.message ?? 'No se pudieron cargar los jugadores.');
      }
    } catch {
      setPlayerError('Error de conexión.');
    } finally {
      setLoadingPlayers(false);
    }
  }, [supabaseUrl]);

  // ── Fetch players (all leagues) ────────────────────────────────────────────
  const fetchAllLeagues = useCallback(async (query: string, category: string) => {
    setLoadingAllLeagues(true);
    try {
      const results = await Promise.all(
        LEAGUES.map(async (lg) => {
          const res  = await fetch(`${supabaseUrl}/functions/v1/football-stats?type=players&league=${lg.slug}&category=${category}&season=2024`);
          const data = await res.json();
          if (data.status === 'ok' && Array.isArray(data.data)) {
            return (data.data as PlayerStat[]).map((p) => ({
              ...p,
              _leagueFlag:  lg.flag,
              _leagueLabel: lg.label,
            }));
          }
          return [] as PlayerStat[];
        })
      );
      const flat = results.flat();
      const q = query.toLowerCase().trim();
      const filtered = q
        ? flat.filter((p) =>
            p.name.toLowerCase().includes(q) ||
            (p.shortName ?? '').toLowerCase().includes(q) ||
            (p.teamName ?? '').toLowerCase().includes(q)
          )
        : flat;
      const sortKey2 =
        category === 'assists'     ? 'assists'
        : category === 'shots'    ? 'shots'
        : category === 'saves'    ? 'saves'
        : category === 'yellowCards' ? 'yellowCards'
        : 'goals';
      filtered.sort((a, b) => ((b as any)[sortKey2] ?? 0) - ((a as any)[sortKey2] ?? 0));
      setAllLeaguesPlayers(filtered.slice(0, 100));
    } catch { /* ignore */ }
    finally { setLoadingAllLeagues(false); }
  }, [supabaseUrl]);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchTeams(activeLeague.slug); }, [activeLeague.slug, fetchTeams]);

  useEffect(() => {
    if (subTab === 'jugadores' && !allLeaguesMode) {
      fetchPlayers(activeLeague.slug, playerCat);
    }
  }, [activeLeague.slug, playerCat, subTab, allLeaguesMode, fetchPlayers]);

  // When switching to all-leagues mode, fetch immediately
  useEffect(() => {
    if (allLeaguesMode && isPremium) {
      fetchAllLeagues(playerSearch, playerCat);
    }
  }, [allLeaguesMode, playerCat, fetchAllLeagues, isPremium]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const sortedTeams = [...teams].sort((a, b) => {
    const va = (a as any)[sortKey] ?? 0;
    const vb = (b as any)[sortKey] ?? 0;
    return sortAsc ? va - vb : vb - va;
  });

  const basePlayers = allLeaguesMode ? allLeaguesPlayers : players;
  const q = playerSearch.toLowerCase().trim();
  const filteredPlayers = q && !allLeaguesMode
    ? basePlayers.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        (p.shortName ?? '').toLowerCase().includes(q) ||
        (p.teamName ?? '').toLowerCase().includes(q)
      )
    : basePlayers;

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const val = playerCat as keyof PlayerStat;
    return ((b[val] as number) ?? 0) - ((a[val] as number) ?? 0);
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(false); }
  };

  const handleSearchSubmit = () => {
    if (!isPremium) { handleUpgrade(); return; }
    if (playerSearch.trim() && !allLeaguesMode) {
      setAllLeaguesMode(true);
      fetchAllLeagues(playerSearch, playerCat);
    }
  };

  const clearSearch = () => {
    setPlayerSearch('');
    setAllLeaguesMode(false);
    setAllLeaguesPlayers([]);
  };

  const ThBtn = ({ label, sk }: { label: string; sk: SortKey }) => (
    <th
      onClick={() => isPremium ? handleSort(sk) : handleUpgrade()}
      className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap transition-colors ${
        sortKey === sk ? 'text-[#00e87a]' : 'text-[#6b7280] hover:text-[#a8b2c1]'
      }`}
    >
      {label}
      {sortKey === sk && (
        <i className={`ml-1 ${sortAsc ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
      )}
    </th>
  );

  const posAbbr = (pos: string) => {
    const p = pos.toLowerCase();
    if (p.includes('portero') || p.includes('goalkeeper') || p === 'gk') return 'POR';
    if (p.includes('defensa') || p.includes('defender') || p === 'cb' || p === 'lb' || p === 'rb') return 'DEF';
    if (p.includes('centrocampista') || p.includes('midfielder') || p === 'cm') return 'MED';
    if (p.includes('delantero') || p.includes('forward') || p === 'st' || p === 'cf') return 'DEL';
    return pos.substring(0, 3).toUpperCase() || '—';
  };

  return (
    <div className="space-y-5">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#ffd700]/15">
            <i className="ri-database-2-line text-[#ffd700] text-xl"></i>
          </div>
          <div>
            <h3 className="text-white font-black text-base">Stats Pro — Datos Completos</h3>
            <p className="text-[#6b7280] text-xs">football-data.org · Equipos, jugadores y fichas · Las 5 grandes ligas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/20 text-[#00e87a] text-xs font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
            football-data.org
          </span>
          <PlanBadge plan={plan ?? 'free'} />
        </div>
      </div>

      {/* ── League selector ────────────────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap">
        {LEAGUES.map((lg) => (
          <button
            key={lg.slug}
            onClick={() => { setActiveLeague(lg); if (allLeaguesMode) clearSearch(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeLeague.slug === lg.slug && !allLeaguesMode
                ? 'bg-[#ffd700]/15 text-[#ffd700] border border-[#ffd700]/30'
                : 'bg-[#151719] text-[#a8b2c1] border border-[#2a2d3a] hover:text-white hover:border-[#3d4149]'
            }`}
          >
            <span>{lg.flag}</span>
            <span>{lg.label}</span>
          </button>
        ))}
      </div>

      {/* ── Sub-tabs ───────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 w-fit">
        {([
          { key: 'equipos',   label: 'Equipos',   icon: 'ri-shield-line' },
          { key: 'jugadores', label: 'Jugadores', icon: 'ri-user-star-line' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              subTab === t.key ? 'bg-[#ffd700]/15 text-[#ffd700]' : 'text-[#6b7280] hover:text-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${t.icon} text-sm`}></i>
            </div>
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* EQUIPOS TAB                                                     */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {subTab === 'equipos' && (
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between flex-wrap gap-2">
            <h4 className="text-white font-bold text-sm flex items-center gap-2">
              {activeLeague.flag} Clasificación {activeLeague.label}
              <span className="text-[#6b7280] font-normal text-xs">Temporada 24/25</span>
            </h4>
            {isPremium && teamsSource === 'football-data.org' && (
              <span className="text-[#3d4149] text-xs">Haz clic en un equipo para ver su ficha</span>
            )}
          </div>

          {loadingTeams ? (
            <div className="animate-pulse">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-[#1e2028]">
                  <div className="h-4 w-5 bg-[#2a2d3a] rounded"></div>
                  <div className="w-8 h-8 rounded-full bg-[#2a2d3a]"></div>
                  <div className="flex-1 h-4 bg-[#2a2d3a] rounded max-w-[150px]"></div>
                  <div className="ml-auto flex gap-4">
                    {Array.from({ length: 7 }).map((_, j) => <div key={j} className="h-4 w-8 bg-[#2a2d3a] rounded"></div>)}
                  </div>
                </div>
              ))}
            </div>
          ) : teamError ? (
            <div className="px-5 py-6 flex items-center gap-3">
              <div className="w-4 h-4 flex items-center justify-center text-[#ef4444]">
                <i className="ri-error-warning-line text-base"></i>
              </div>
              <p className="text-[#ef4444] text-sm">{teamError}</p>
              <button
                onClick={() => fetchTeams(activeLeague.slug)}
                className="ml-auto text-xs text-[#6b7280] hover:text-white cursor-pointer"
              >
                Reintentar
              </button>
            </div>
          ) : !isPremium ? (
            <LockOverlay onUpgrade={handleUpgrade} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-[#1e2028]">
                    <th className="w-10 px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">#</th>
                    <th className="px-5 py-3 text-left text-[#6b7280] text-xs font-semibold uppercase">Equipo</th>
                    <ThBtn label="PJ" sk="played" />
                    <ThBtn label="G"  sk="won" />
                    <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">E</th>
                    <th className="px-3 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">P</th>
                    <ThBtn label="GF"  sk="goalsFor" />
                    <ThBtn label="GC"  sk="goalsAgainst" />
                    <ThBtn label="+/-" sk="goalDiff" />
                    <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">Forma</th>
                    <ThBtn label="Pts" sk="points" />
                    {teamsSource === 'football-data.org' && (
                      <th className="w-10 px-3 py-3"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map((team, i) => {
                    const clickable = teamsSource === 'football-data.org' && !!team.teamId;
                    return (
                      <tr
                        key={team.team}
                        onClick={clickable ? () => setSelectedTeam({ teamId: team.teamId!, teamName: team.team, teamCrest: team.crest }) : undefined}
                        className={`border-b border-[#1e2028]/50 transition-colors ${clickable ? 'cursor-pointer hover:bg-white/[0.04]' : 'hover:bg-white/[0.02]'}`}
                      >
                        <td className="px-4 py-3.5 text-center">
                          <span className={`text-sm font-black ${i === 0 ? 'text-[#ffd700]' : i <= 2 ? 'text-[#00e87a]' : 'text-[#3d4149]'}`}>
                            {team.position}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            {team.crest && !imgErrors[team.team] ? (
                              <img
                                src={team.crest}
                                alt=""
                                className="w-7 h-7 object-contain flex-shrink-0"
                                onError={() => setImgErrors((p) => ({ ...p, [team.team]: true }))}
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-[#2a2d3a] flex items-center justify-center flex-shrink-0">
                                <i className="ri-shield-line text-[#6b7280] text-xs"></i>
                              </div>
                            )}
                            <span translate="no" className="text-white text-sm font-semibold">{team.team}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3.5 text-center text-[#a8b2c1] text-sm tabular-nums">{team.played}</td>
                        <td className="px-3 py-3.5 text-center text-[#00e87a] text-sm font-bold tabular-nums">{team.won}</td>
                        <td className="px-3 py-3.5 text-center text-[#ffd700] text-sm tabular-nums">{team.draw}</td>
                        <td className="px-3 py-3.5 text-center text-[#ef4444] text-sm tabular-nums">{team.lost}</td>
                        <td className="px-3 py-3.5 text-center text-[#a8b2c1] text-sm tabular-nums">{team.goalsFor}</td>
                        <td className="px-3 py-3.5 text-center text-[#a8b2c1] text-sm tabular-nums">{team.goalsAgainst}</td>
                        <td className="px-3 py-3.5 text-center text-sm font-bold tabular-nums">
                          <span className={team.goalDiff > 0 ? 'text-[#00e87a]' : team.goalDiff < 0 ? 'text-[#ef4444]' : 'text-[#6b7280]'}>
                            {team.goalDiff > 0 ? '+' : ''}{team.goalDiff}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-0.5 justify-center">
                            {(team.form ?? []).slice(-5).map((r, fi) => (
                              <FormDot key={fi} result={r} />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="text-white font-black text-sm tabular-nums">{team.points}</span>
                        </td>
                        {teamsSource === 'football-data.org' && (
                          <td className="px-3 py-3.5 text-center">
                            {clickable && (
                              <div className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-arrow-right-s-line text-[#3d4149] group-hover:text-[#6b7280] text-base"></i>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-5 py-3 border-t border-[#1e2028] flex items-center justify-between text-xs text-[#3d4149] flex-wrap gap-2">
            <span>
              Fuente: {teamsSource === 'football-data.org' ? 'football-data.org' : 'ESPN (fallback)'} · Temporada 2024/25
            </span>
            {teams.length > 0 && <span>{teams.length} equipos</span>}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* JUGADORES TAB — PRÓXIMAMENTE                                   */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {subTab === 'jugadores' && (
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-14 text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-[#ff6b35]/10 mx-auto mb-6">
            <i className="ri-user-star-line text-[#ff6b35] text-3xl"></i>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ff6b35]/10 border border-[#ff6b35]/25 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff6b35] animate-pulse inline-block"></span>
            <span className="text-[#ff6b35] text-xs font-bold uppercase tracking-widest">Próximamente</span>
          </div>
          <h3 className="text-white font-black text-xl mb-3">Estadísticas de Jugadores — Stats Pro</h3>
          <p className="text-[#a8b2c1] text-sm max-w-md mx-auto mb-6 leading-relaxed">
            Pronto disponible: clasificaciones completas de jugadores con goles, asistencias, tarjetas,
            minutos jugados, tiros, paradas y rendimiento individual en las 5 grandes ligas europeas.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              { icon: 'ri-football-line', label: 'Goles & Asistencias', color: '#00e87a' },
              { icon: 'ri-shield-line',   label: 'Paradas & Porteros',  color: '#60a5fa' },
              { icon: 'ri-cursor-line',   label: 'Tiros a puerta',      color: '#ff6b35' },
              { icon: 'ri-flashlight-line', label: 'Tarjetas & Fair Play', color: '#f59e0b' },
              { icon: 'ri-global-line',   label: 'Búsqueda multi-liga', color: '#ffd700' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold"
                style={{ borderColor: `${item.color}30`, background: `${item.color}08`, color: item.color }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                {item.label}
              </div>
            ))}
          </div>
          <p className="text-[#3d4149] text-xs">
            <i className="ri-time-line mr-1"></i>En desarrollo · Disponible en la próxima actualización
          </p>
        </div>
      )}

      {/* ── Team Detail Modal ──────────────────────────────────────── */}
      {selectedTeam && (
        <TeamDetailModal
          teamId={selectedTeam.teamId}
          teamName={selectedTeam.teamName}
          teamCrest={selectedTeam.teamCrest}
          league={activeLeague.slug}
          onClose={() => setSelectedTeam(null)}
        />
      )}
    </div>
  );
}
